import React, { useMemo, useState } from 'react';
import {
  Button,
  Divider,
  Label,
  LabelGroup,
  MenuToggle,
  MenuToggleCheckbox,
  MenuToggleElement,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { TimesIcon } from '@patternfly/react-icons';
import { CursorPagination } from '../common';

const INCLUDE_CLOSED_VALUE = '__include_closed__';

interface IssuesToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedStatuses: string[];
  availableStatuses: string[];
  onStatusesChange: (statuses: string[]) => void;
  selectedLabels: string[];
  availableLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  onClearAllFilters: () => void;
  isStatusSelectOpen: boolean;
  onStatusSelectToggle: (isOpen: boolean) => void;
  showAllStatuses: boolean;
  onShowAllStatusesChange: (checked: boolean) => void;
  // Cursor pagination props
  pageStartIndex: number;
  itemsOnPage: number;
  totalSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onFirstPage: () => void;
  perPage: number;
  onPerPageChange: (perPage: number) => void;
  isLoading: boolean;
}

export const IssuesToolbar: React.FC<IssuesToolbarProps> = ({
  searchValue,
  onSearchChange,
  selectedStatuses,
  availableStatuses,
  onStatusesChange,
  selectedLabels,
  availableLabels,
  onLabelsChange,
  onClearAllFilters,
  isStatusSelectOpen,
  onStatusSelectToggle,
  showAllStatuses,
  onShowAllStatusesChange,
  pageStartIndex,
  itemsOnPage,
  totalSize,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onFirstPage,
  perPage,
  onPerPageChange,
  isLoading,
}) => {
  const [statusSearchValue, setStatusSearchValue] = useState('');
  const [isLabelsOpen, setIsLabelsOpen] = useState(false);
  const [labelSearchValue, setLabelSearchValue] = useState('');

  // Status filter
  const handleStatusSelect = (_event: React.MouseEvent | undefined, value: string | number | undefined) => {
    if (value === undefined) return;
    const status = String(value);

    if (status === INCLUDE_CLOSED_VALUE) {
      onShowAllStatusesChange(!showAllStatuses);
      return;
    }
    if (status === '__select_all__') {
      onStatusesChange([...availableStatuses]);
      return;
    }
    if (status === '__deselect_all__') {
      onStatusesChange([]);
      return;
    }

    const newSelected = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status];
    onStatusesChange(newSelected);
  };

  const filteredStatuses = useMemo(() => {
    if (!statusSearchValue) return availableStatuses;
    const searchLower = statusSearchValue.toLowerCase();
    return availableStatuses.filter((status) => status.toLowerCase().includes(searchLower));
  }, [availableStatuses, statusSearchValue]);

  const allStatusesSelected = selectedStatuses.length === availableStatuses.length && availableStatuses.length > 0;
  const noStatusesSelected = selectedStatuses.length === 0;
  const statusesCheckboxState: boolean | null = noStatusesSelected ? false : allStatusesSelected ? true : null;

  const handleStatusesCheckboxClick = () => {
    if (statusesCheckboxState === true) {
      onStatusesChange([]);
    } else {
      onStatusesChange([...availableStatuses]);
    }
  };

  const statusToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      onClick={() => onStatusSelectToggle(!isStatusSelectOpen)}
      isExpanded={isStatusSelectOpen}
      isFullWidth
      splitButtonItems={[
        <MenuToggleCheckbox
          key="status-checkbox"
          id="dashboard-status-select-checkbox"
          isChecked={statusesCheckboxState}
          onChange={handleStatusesCheckboxClick}
          aria-label="Select all statuses"
        />,
      ]}
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={statusSearchValue}
          onClick={() => onStatusSelectToggle(true)}
          onChange={(_event, value) => setStatusSearchValue(value)}
          autoComplete="off"
          placeholder={selectedStatuses.length > 0 ? `${selectedStatuses.length} selected` : 'Filter statuses...'}
          aria-label="Filter statuses"
        />
        <TextInputGroupUtilities>
          {statusSearchValue && (
            <Button
              variant="plain"
              onClick={() => setStatusSearchValue('')}
              aria-label="Clear filter"
              style={{ padding: '0 4px' }}
            >
              <TimesIcon />
            </Button>
          )}
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  const handleStatusOpenChange = (open: boolean) => {
    onStatusSelectToggle(open);
    if (!open) {
      setStatusSearchValue('');
    }
  };

  // Labels filter
  const handleLabelSelect = (_event: React.MouseEvent | undefined, value: string | number | undefined) => {
    if (value === undefined) return;
    const label = String(value);

    if (label === '__select_all__') {
      onLabelsChange([...availableLabels]);
      return;
    }
    if (label === '__deselect_all__') {
      onLabelsChange([]);
      return;
    }

    const newSelected = selectedLabels.includes(label)
      ? selectedLabels.filter((l) => l !== label)
      : [...selectedLabels, label];
    onLabelsChange(newSelected);
  };

  const filteredLabels = useMemo(() => {
    if (!labelSearchValue) return availableLabels;
    const searchLower = labelSearchValue.toLowerCase();
    return availableLabels.filter((label) => label.toLowerCase().includes(searchLower));
  }, [availableLabels, labelSearchValue]);

  const allLabelsSelected = selectedLabels.length === availableLabels.length && availableLabels.length > 0;
  const noLabelsSelected = selectedLabels.length === 0;
  const labelsCheckboxState: boolean | null = noLabelsSelected ? false : allLabelsSelected ? true : null;

  const handleLabelsCheckboxClick = () => {
    if (labelsCheckboxState === true) {
      onLabelsChange([]);
    } else {
      onLabelsChange([...availableLabels]);
    }
  };

  const labelsToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      onClick={() => setIsLabelsOpen(!isLabelsOpen)}
      isExpanded={isLabelsOpen}
      isFullWidth
      splitButtonItems={[
        <MenuToggleCheckbox
          key="labels-checkbox"
          id="dashboard-labels-select-checkbox"
          isChecked={labelsCheckboxState}
          onChange={handleLabelsCheckboxClick}
          aria-label="Select all labels"
        />,
      ]}
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={labelSearchValue}
          onClick={() => setIsLabelsOpen(true)}
          onChange={(_event, value) => setLabelSearchValue(value)}
          autoComplete="off"
          placeholder={selectedLabels.length > 0 ? `${selectedLabels.length} selected` : 'Filter labels...'}
          aria-label="Filter labels"
        />
        <TextInputGroupUtilities>
          {labelSearchValue && (
            <Button
              variant="plain"
              onClick={() => setLabelSearchValue('')}
              aria-label="Clear filter"
              style={{ padding: '0 4px' }}
            >
              <TimesIcon />
            </Button>
          )}
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  const handleLabelsOpenChange = (open: boolean) => {
    setIsLabelsOpen(open);
    if (!open) {
      setLabelSearchValue('');
    }
  };

  return (
    <Toolbar
      clearAllFilters={onClearAllFilters}
      clearFiltersButtonText="Clear filters"
      className="issues-toolbar"
    >
      <ToolbarContent>
        <ToolbarGroup variant="filter-group">
          <ToolbarItem>
            <SearchInput
              placeholder="Search by key or summary"
              value={searchValue}
              onChange={(_event, value) => onSearchChange(value)}
              onClear={() => onSearchChange('')}
              aria-label="Search issues"
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              role="menu"
              isOpen={isStatusSelectOpen}
              selected={selectedStatuses}
              onSelect={handleStatusSelect}
              onOpenChange={handleStatusOpenChange}
              toggle={statusToggle}
              aria-label="Filter by status"
            >
              <SelectList>
                <SelectOption
                  key={INCLUDE_CLOSED_VALUE}
                  value={INCLUDE_CLOSED_VALUE}
                  hasCheckbox
                  isSelected={showAllStatuses}
                >
                  Include closed
                </SelectOption>
                <Divider />
                {availableStatuses.length > 0 && (
                  <>
                    <SelectOption
                      key="__select_all__"
                      value="__select_all__"
                      isDisabled={allStatusesSelected}
                    >
                      Select all
                    </SelectOption>
                    <SelectOption
                      key="__deselect_all__"
                      value="__deselect_all__"
                      isDisabled={noStatusesSelected}
                    >
                      Deselect all
                    </SelectOption>
                    <Divider />
                  </>
                )}
                {filteredStatuses.length === 0 ? (
                  <SelectOption isDisabled>No statuses found</SelectOption>
                ) : (
                  filteredStatuses.map((status) => (
                    <SelectOption
                      key={status}
                      value={status}
                      hasCheckbox
                      isSelected={selectedStatuses.includes(status)}
                    >
                      {status}
                    </SelectOption>
                  ))
                )}
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Select
              role="menu"
              toggle={labelsToggle}
              onSelect={handleLabelSelect}
              selected={selectedLabels}
              isOpen={isLabelsOpen}
              onOpenChange={handleLabelsOpenChange}
            >
              <SelectList>
                {availableLabels.length > 0 && (
                  <>
                    <SelectOption
                      key="__select_all__"
                      value="__select_all__"
                      isDisabled={allLabelsSelected}
                    >
                      Select all
                    </SelectOption>
                    <SelectOption
                      key="__deselect_all__"
                      value="__deselect_all__"
                      isDisabled={noLabelsSelected}
                    >
                      Deselect all
                    </SelectOption>
                    <Divider />
                  </>
                )}
                {filteredLabels.length === 0 ? (
                  <SelectOption isDisabled>No labels found</SelectOption>
                ) : (
                  filteredLabels.map((label) => (
                    <SelectOption
                      key={label}
                      value={label}
                      hasCheckbox
                      isSelected={selectedLabels.includes(label)}
                    >
                      {label}
                    </SelectOption>
                  ))
                )}
              </SelectList>
            </Select>
          </ToolbarItem>
          {selectedStatuses.length > 0 && (
            <ToolbarItem>
              <LabelGroup categoryName="Status">
                {selectedStatuses.map((status) => (
                  <Label key={status} onClose={() => onStatusesChange(selectedStatuses.filter((s) => s !== status))}>
                    {status}
                  </Label>
                ))}
              </LabelGroup>
            </ToolbarItem>
          )}
          {selectedLabels.length > 0 && (
            <ToolbarItem>
              <LabelGroup categoryName="Labels">
                {selectedLabels.map((label) => (
                  <Label key={label} color="blue" onClose={() => onLabelsChange(selectedLabels.filter((l) => l !== label))}>
                    {label}
                  </Label>
                ))}
              </LabelGroup>
            </ToolbarItem>
          )}
        </ToolbarGroup>
        <ToolbarItem variant="pagination" align={{ default: 'alignEnd' }}>
          <CursorPagination
            pageStartIndex={pageStartIndex}
            itemsOnPage={itemsOnPage}
            totalSize={totalSize}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
            onFirstPage={onFirstPage}
            perPage={perPage}
            onPerPageChange={onPerPageChange}
            isDisabled={isLoading}
          />
        </ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );
};
